import { useEffect, useMemo, useState } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { red } from '@mui/material/colors'
import { useValidation, UseValidationProps } from 'hooks/useValidation'

type Props = UseValidationProps & {
  value: string
  onChange: (value: string) => void
  setValid: (valid: boolean) => void
  placeholder?: string | null
  errorMessage?: string | null
}
export const PasswordInput = ({ value, onChange, placeholder, errorMessage, setValid, ...props }: Props) => {
  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword(show => !show)
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const { register, setValue, helperText, maxLength, required, validate, error, isValid } = useValidation(props)

  const passwordError = useMemo(() => error || !!errorMessage, [error, errorMessage])

  useEffect(() => setValue('value', value), [value])
  useEffect(() => {
    setValid(!passwordError && isValid)
  }, [passwordError, isValid])

  return (
    <TextField
      hiddenLabel
      fullWidth
      type={showPassword ? 'text' : 'password'}
      variant="outlined"
      value={value}
      placeholder={placeholder ?? ''}
      error={passwordError}
      helperText={helperText || errorMessage}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              color={passwordError ? 'error' : 'default'}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...register('value', {
        onChange: e => onChange(e.target.value),
        maxLength,
        required,
        validate,
      })}
      sx={{
        '& .MuiInputBase-root': {
          backgroundColor: passwordError ? red[50] : '#FFF',
        },
      }}
    />
  )
}
